import React, { useState } from 'react';
import Icon from './Icon';
import Logo from './Logo';

/**
 * Unified state-display primitive. Renders every dead-end the app can
 * hit — missing project, forbidden, session expired, 5xx, network
 * error, no-data-yet, deploy/maintenance, planned-feature — with the
 * same visual pattern. Drives the route-level + tab-level error
 * boundaries plus every empty-state surface (lists / tables with
 * nothing in them).
 *
 * Two variants:
 *   in-chrome  — centered inside the tab body, project chrome stays
 *                above. Used when sub-resources fail or empty.
 *   full-page  — replaces the entire viewport with a centered card
 *                under the E3 Summit brand mark. Used for auth, 404,
 *                project-not-found, server-down (no chrome possible).
 *
 * Eight `kind`s, each with its own icon + color tone:
 *   not-found / forbidden / auth-expired / server-error / network-error
 *   / empty / maintenance / coming-soon
 *
 * See README in design_handoff_state_system for the full spec.
 */

const KIND_CONFIG = {
  'not-found':    { icon: 'map-pin-off',    tone: 'warning' },
  'forbidden':    { icon: 'lock',           tone: 'danger' },
  'auth-expired': { icon: 'log-in',         tone: 'warning' },
  'server-error': { icon: 'alert-triangle', tone: 'danger' },
  'network-error':{ icon: 'wifi-off',       tone: 'warning' },
  'empty':        { icon: 'inbox',          tone: 'neutral' },
  'maintenance':  { icon: 'wrench',         tone: 'neutral' },
  'coming-soon':  { icon: 'sparkles',       tone: 'accent' },
};

const TONE_STYLE = {
  warning: {
    bg: 'var(--warning-soft)',
    ring: 'color-mix(in oklch, var(--warning) 22%, transparent)',
    fg: 'var(--warning)',
  },
  danger: {
    bg: 'var(--danger-soft)',
    ring: 'color-mix(in oklch, var(--danger) 22%, transparent)',
    fg: 'var(--danger)',
  },
  neutral: {
    bg: 'var(--bg-surface-2)',
    ring: 'var(--border-subtle)',
    fg: 'var(--text-3)',
  },
  accent: {
    bg: 'var(--accent-soft)',
    ring: 'color-mix(in oklch, var(--accent) 22%, transparent)',
    fg: 'var(--accent-text)',
  },
};

export default function StateScreen({
  kind,
  variant = 'in-chrome',
  title,
  body,
  primary = null,
  secondary = null,
  errorCode = null,
}) {
  const config = KIND_CONFIG[kind] || KIND_CONFIG['empty'];
  const tone = TONE_STYLE[config.tone];

  const inner = (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: variant === 'full-page' ? 480 : 520,
        padding: variant === 'in-chrome' ? '48px 24px' : 24,
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Icon tile */}
      <div
        aria-hidden="true"
        style={{
          width: 64,
          height: 64,
          borderRadius: 12,
          background: tone.bg,
          border: `1px solid ${tone.ring}`,
          color: tone.fg,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <Icon name={config.icon} size={28} stroke={1.6} />
      </div>

      {/* Title */}
      <h1
        style={{
          margin: '24px 0 8px',
          fontSize: 23.5,
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: '-0.02em',
          color: 'var(--text-1)',
        }}
      >
        {title}
      </h1>

      {/* Body */}
      <p
        style={{
          margin: 0,
          fontSize: 15.5,
          lineHeight: 1.55,
          color: 'var(--text-2)',
          letterSpacing: '-0.005em',
          maxWidth: 480,
        }}
      >
        {body}
      </p>

      {/* CTA row */}
      {(primary || secondary) && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 24,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {primary && <PrimaryCTA cta={primary} />}
          {secondary && <SecondaryCTA cta={secondary} />}
        </div>
      )}

      {/* Optional error-code footer */}
      {errorCode && <ErrorCodeFooter code={errorCode} />}
    </div>
  );

  if (variant === 'full-page') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-page)',
          color: 'var(--text-1)',
        }}
      >
        {/* Brand header — keeps the user oriented; they're still in
            our app, just somewhere unfortunate. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '20px 24px',
          }}
        >
          <Logo size={28} />
          <span
            style={{
              fontSize: 15.5,
              fontWeight: 600,
              color: 'var(--text-1)',
              letterSpacing: '-0.01em',
            }}
          >
            E3 Summit
          </span>
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {inner}
        </div>
      </div>
    );
  }

  // In-chrome: centered inside the tab body, chrome stays above.
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 360,
      }}
    >
      {inner}
    </div>
  );
}

/* -------------------- CTA buttons -------------------- */

function ctaContent(cta) {
  return (
    <>
      {cta.icon && <Icon name={cta.icon} size={14} stroke={cta.iconStroke || 1.8} />}
      <span>{cta.label}</span>
    </>
  );
}

function PrimaryCTA({ cta }) {
  const style = {
    height: 36,
    padding: '0 14px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    color: '#fff',
    background: 'var(--accent)',
    boxShadow:
      '0 1px 0 color-mix(in oklch, var(--accent) 40%, black), 0 1px 2px oklch(0.3 0.05 135 / 0.18)',
    border: 0,
    gap: 6,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    textDecoration: 'none',
    fontFamily: 'var(--font-sans)',
  };
  if (cta.href) {
    return <a href={cta.href} style={style} onClick={cta.onClick}>{ctaContent({ ...cta, iconStroke: 2 })}</a>;
  }
  return <button type="button" style={style} onClick={cta.onClick}>{ctaContent({ ...cta, iconStroke: 2 })}</button>;
}

function SecondaryCTA({ cta }) {
  const style = {
    height: 36,
    padding: '0 12px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    color: 'var(--text-2)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    gap: 6,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    textDecoration: 'none',
    fontFamily: 'var(--font-sans)',
  };
  if (cta.href) {
    return <a href={cta.href} style={style} onClick={cta.onClick}>{ctaContent(cta)}</a>;
  }
  return <button type="button" style={style} onClick={cta.onClick}>{ctaContent(cta)}</button>;
}

/* -------------------- Error-code footer (click to copy) -------------------- */

function ErrorCodeFooter({ code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard might be unavailable on http:// — fall back silently
    }
  };
  return (
    <>
      <div
        aria-hidden
        style={{
          width: 80,
          height: 1,
          background: 'var(--border-subtle)',
          marginTop: 24,
        }}
      />
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy error code"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: 'var(--text-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          background: 'transparent',
          border: 0,
          padding: '2px 4px',
          borderRadius: 4,
          cursor: 'copy',
          marginTop: 12,
        }}
      >
        {copied ? '✓ Copied' : code}
      </button>
    </>
  );
}
